import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import type { Level, RawLevel } from '@/types';
import { getAuthHeaders } from '@/utils/getHeaders';
import { levelAdapter } from '@/adapters/adapters';
import { usePlayerStore } from './player';

export const useLevelsStore = defineStore('levels', () => {
    const playerStore = usePlayerStore();
    const player = computed(() => playerStore.player);
    const levels = ref<Level[]>([]);
    const valueNextLevel = 1;
    const totalSumBet = ref<number>(0);
    const loadingFlag = ref<boolean>(false);
    const displayProgressValue = computed(() => progressValue.value - 1);
    const previousLevel = computed(() => {
        const findPreviousLevel = levels.value.find((item) => item.order === player.value.level.order - valueNextLevel);
        return findPreviousLevel || levels.value[0];
    });
    const nextLevel = computed(() => {
        const findNextLevel = levels.value.find((item) => item.order === player.value.level.order + valueNextLevel);
        return findNextLevel || levels.value[0];
    });
    const progressValue = computed(() => {
        if (!totalSumBet.value) return 1;
        const startProgress = player.value.level?.cumulative_cost || 1;
        const endProgress = nextLevel.value?.cumulative_cost || 1;
        const currentProgress = totalSumBet.value - startProgress;
        const cumulativeCost = endProgress - startProgress;
        return Math.max(1, Math.round((currentProgress / cumulativeCost) * 100));
    });
    function toUpdateLevelPlayer() {
        const findLevel = levels.value.find((item) => item.order === player.value.level.order);
        if (findLevel) {
            player.value.level = findLevel; // Direct assignment instead of using toUpdatePlayerField
        }
    }
    function toSetFlip(id: number) {
        levels.value.forEach((item) => {
            if (item.id === id) {
                item.flip = !item.flip;
            }
        });
    }
    function toUpNextLevel() {
        levels.value.forEach((item) => {
            if (item.order === player.value.level.order) {
                item.current = true;
                item.is_completed = false;
                item.is_locked = false;
            } else if (item.order < player.value.level.order) {
                item.is_completed = true;
                item.is_locked = false;
                item.current = false;
            } else if (item.order > player.value.level.order) {
                item.is_locked = true;
                item.is_completed = false;
                item.current = false;
            }
        });
    }

    const awaitGetLevels = async () => {
        loadingFlag.value = true;
        try {
            const response = await axios.get<RawLevel[]>('/main-api/levels');
            if (response.status === 200) {
                levels.value = levelAdapter.mapLevel(response.data, player.value);
                toUpdateLevelPlayer();
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    };
    const awaitGetTotalSumBet = async () => {
        loadingFlag.value = true;
        if (!player.value.id) return;
        try {
            const response = await axios.get<number>(`/int-api/game_sessions/sum_bet_by_user_id/${player.value.id}`, getAuthHeaders());
            // const response = await axios.get<number>(`/int-api/game_sessions/sum_bet_by_user_id/${player.value.id}`);
            if (response.status === 200 && Number.isFinite(response.data)) {
                totalSumBet.value = response.data;
            } else {
                console.error('Error: expected number, got', response.data);
                totalSumBet.value = 0;
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    };
    return { toUpdateLevelPlayer, nextLevel, previousLevel, displayProgressValue, levels, toSetFlip, toUpNextLevel, awaitGetLevels, awaitGetTotalSumBet, progressValue, totalSumBet, loadingFlag };
});
